import { CONFLICT, NOT_FOUND, FORBIDDEN } from '../constants/httpStatusCodes'
import { isGeneratedId, TASK_ADD_SAVE, TASK_ADD_SAVE_SUCCESS, TASK_ADD_SAVE_FAILURE } from './addTask'
import { actions } from 'react-redux-toastr'
import { getErrorAction } from './errorMapper'
import mapValues from 'lodash/mapValues'
import { NOT_FOUND_TYPES_PROJECT } from '../constants/notFoundTypes'
import { ERROR_MESSAGE_TYPE_BOARD_PERMSSIONS } from '../constants/errorMessageTypes'
import { redirectToNotFound } from './redirectToNotFound'
import { redirectToError } from './redirectToError'
import { createReducer } from './reducerUtils'
import { mergeMap, map, catchError } from 'rxjs/operators'
import { ofType } from 'redux-observable'
import { of } from 'rxjs'

// Actions
export const TASKS_LOAD = 'TASKS_LOAD'
export const TASKS_LOAD_SUCCESS = 'TASKS_LOAD_SUCCESS'
export const TASKS_LOAD_FAILURE = 'TASKS_LOAD_FAILURE'

export const TASK_MOVE_NOOP = 'TASK_MOVE_NOOP'
export const TASK_MOVE = 'TASK_MOVE'
export const TASK_MOVE_PERSIST = 'TASK_MOVE_PERSIST'
export const TASK_MOVE_PERSIST_FAILURE = 'TASK_MOVE_PERSIST_FAILURE'
export const TASK_MOVE_PERSIST_SUCCESS = 'TASK_MOVE_PERSIST_SUCCESS'

export const TASK_LOAD_SUCCESS = 'TASK_LOAD_SUCCESS'

export const TASK_DELETE = 'TASK_DELETE'
export const TASK_DELETE_SUCCESS = 'TASK_DELETE_SUCCESS'
export const TASK_DELETE_FAILURE = 'TASK_DELETE_FAILURE'


// Action Creators
export const tasksLoad = projectId => ({ type: TASKS_LOAD, payload: projectId })
export const tasksLoadSuccess = payload => ({ type: TASKS_LOAD_SUCCESS, payload })
export const tasksLoadFailure = payload => ({ type: TASKS_LOAD_FAILURE, payload, error: true })

export const taskLoadSuccess = payload => ({ type: TASK_LOAD_SUCCESS, payload })

export const taskMovePersist = taskPosition => ({ type: TASK_MOVE_PERSIST, payload: taskPosition })
export const taskMovePersistSuccess = payload => ({ type: TASK_MOVE_PERSIST_SUCCESS, payload })
export const taskMovePersistFailure = payload => ({ type: TASK_MOVE_PERSIST_FAILURE, payload, error: true })

export const taskMove = taskPosition => ({ type: TASK_MOVE, payload: taskPosition })
export const taskMoveNoop = () => ({ type: TASK_MOVE_NOOP })

export const taskDelete = (taskId, taskName) => ({ type: TASK_DELETE, payload: { taskId, taskName } })
export const taskDeleteSuccess = payload => ({ type: TASK_DELETE_SUCCESS, payload })
export const taskDeleteFailure = payload => ({ type: TASK_DELETE_FAILURE, payload, error: true })

const isTaskInSamePosition = (state, taskPosition) => {
  if (taskPosition.sourceLaneId !== taskPosition.targetLaneId) {
    return false
  }

  const sourceTaskIndex = state.taskMappings[taskPosition.sourceLaneId].indexOf(taskPosition.taskId)
  const targetTaskIndex = taskPosition.nextTaskId
      ? state.taskMappings[taskPosition.sourceLaneId].indexOf(taskPosition.nextTaskId) - 1
      : state.taskMappings[taskPosition.sourceLaneId].length - 1

  return taskPosition.taskId === taskPosition.nextTaskId || sourceTaskIndex === targetTaskIndex
}

const removeLaneId = (task) => {
  const newTask = Object.assign({}, task)
  delete newTask.laneId
  return newTask
}

// Epics
export const tasksLoadEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(TASKS_LOAD),
    mergeMap(action =>
      vapiClient.getTasks(action.payload).pipe(
        map(ajaxResponse => tasksLoadSuccess(ajaxResponse.response)),
        catchError(error => of(getErrorAction(error, tasksLoadFailure)))
      )
    )
  )

export const tasksLoadFailureEpic = (action$, state$) =>
  action$.pipe(
    ofType(TASKS_LOAD_FAILURE),
    map(action => {
      switch (action.payload.status) {
        case NOT_FOUND:
          return redirectToNotFound(NOT_FOUND_TYPES_PROJECT)
        case FORBIDDEN:
          return redirectToError(ERROR_MESSAGE_TYPE_BOARD_PERMSSIONS)
        default:
          return redirectToError()
      }
    })
  )

export const taskMoveEpic = (action$, state$) =>
  action$.pipe(
    ofType(TASK_MOVE),
    map(action => {
      if (!isGeneratedId(action.payload.nextTaskId) && !isTaskInSamePosition(state$.value, action.payload)) {
        return taskMovePersist(action.payload)
      }
      return taskMoveNoop()
    })
  )

export const taskMovePersistEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(TASK_MOVE_PERSIST),
    mergeMap(action =>
      vapiClient.putTaskPosition(
        state$.value.project.id,
        action.payload.taskId,
        action.payload.targetLaneId,
        action.payload.nextTaskId
      )
      .pipe(
        map(ajaxResponse => taskMovePersistSuccess(ajaxResponse.response)),
        catchError(error => of(getErrorAction(error, taskMovePersistFailure)))
      )
    )
  )

export const taskMovePersistFailureEpic = (action$, state$) =>
  action$.pipe(
    ofType(TASK_MOVE_PERSIST_FAILURE),
    map(() => tasksLoad(state$.value.project.id))
  )

export const taskDeleteEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(TASK_DELETE),
    mergeMap(action =>
      vapiClient.deleteTask(state$.value.project.id, action.payload.taskId).pipe(
        map(() => taskDeleteSuccess(action.payload)),
        catchError(error => of(getErrorAction(error, taskDeleteFailure, action.payload)))
      )
    )
  )

export const taskDeleteSuccessEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(TASK_DELETE_SUCCESS),
    map((action) => {
      const taskDeleteSuccessMessage = localize(state$.value.resources, 'taskBoards.Msg_NamedTaskDeleted')
        .replace('{taskName}', action.payload.taskName)
      const taskDeleteSuccessToast = {
        type: 'success',
        message: taskDeleteSuccessMessage,
        options: {
          removeOnHover: true
        }
      }

      return actions.add(taskDeleteSuccessToast)
    })
  )

export const taskDeleteFailureEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(TASK_DELETE_FAILURE),
    map((action) => {
      const { taskName, status } = action.payload

      let taskDeleteErrorMessage = localize(state$.value.resources, 'taskBoards.Msg_TaskDeleteFailed')

      if (status === CONFLICT) {
        taskDeleteErrorMessage = localize(state$.value.resources, 'taskBoards.Msg_TaskDeleteFailedDueToActualWork')
      }

      taskDeleteErrorMessage = taskDeleteErrorMessage.replace('{taskName}', taskName)

      const taskDeleteFailureToast = {
        type: 'error',
        message: taskDeleteErrorMessage,
        options: {
          removeOnHover: true
        }
      }

      return actions.add(taskDeleteFailureToast)
    })
  )

// TasksReducer
const taskLoadSuccessReducer = (state, payload) => ({ ...state, [payload.id]: removeLaneId(payload) })

const tasksLoadSuccessReducer = (state, tasks) => tasks.reduce((map, task) => {
  map[task.id] = removeLaneId(task)
  return map
}, {})

const taskAddSaveReducer = (state, { generatedId, taskName }) => ({
  ...state,
  [generatedId]: {
    id: generatedId,
    name: taskName,
    assignments: []
  }
})

const taskAddSaveSuccessReducer = (state, { generatedId, savedTask }) => {
  const newState = {
    ...state,
    [savedTask.id]: removeLaneId(savedTask)
  }
  delete newState[generatedId]
  return newState
}

const taskAddSaveFailureReducer = (state, { generatedId }) =>{
  const newState = { ...state }
  delete newState[generatedId]
  return newState
}

const taskDeleteSuccessReducer = (state, { taskId }) => {
  const newState = { ...state }
  delete newState[taskId]
  return newState
}

const tasksHandlerMapping = {
  [TASK_LOAD_SUCCESS]: taskLoadSuccessReducer,
  [TASKS_LOAD_SUCCESS]: tasksLoadSuccessReducer,
  [TASK_ADD_SAVE]: taskAddSaveReducer,
  [TASK_ADD_SAVE_SUCCESS]: taskAddSaveSuccessReducer,
  [TASK_ADD_SAVE_FAILURE]: taskAddSaveFailureReducer,
  [TASK_DELETE_SUCCESS]: taskDeleteSuccessReducer
}

const tasksInitialState = null
export const tasksReducer = createReducer(tasksInitialState, tasksHandlerMapping)

// Task Mappings Reducer
const taskLoadSuccessMappingsReducer = (state, tasks) => tasks.reduce((map, task) => {
  map[task.laneId] = map[task.laneId] || []
  map[task.laneId] = [ ...map[task.laneId], task.id ]
  return map
}, {})

const taskAddSaveMappingsReducer = (state, { generatedId, laneId }) => ({
  ...state,
  [laneId]: state[laneId] ? [generatedId, ...state[laneId]] : [generatedId]
})

const taskAddSaveSuccessMappingsReducer = (state, { savedTask, generatedId }) => {
  const laneId = savedTask.laneId
  const tasksInLane = [...state[laneId]]
  const generatedIdIndex = state[laneId].indexOf(generatedId)
  tasksInLane[generatedIdIndex] = savedTask.id
  return {
    ...state,
    [laneId]: tasksInLane
  }
}

const taskAddSaveFailureMappingsReducer = (state, { generatedId, laneId }) => ({
  ...state,
  [laneId]: state[laneId].filter((taskId) => taskId !== generatedId)
})

const taskMovePersistMappingsReducer = (state, { taskId, nextTaskId, sourceLaneId, targetLaneId }) => {
  const isIntraLaneMove = sourceLaneId === targetLaneId
  const laneWithRemovedTask = state[sourceLaneId].filter((item, index) => item !== taskId)
  let targetLane = isIntraLaneMove ? laneWithRemovedTask : state[targetLaneId]
  targetLane = targetLane || []
  const targetIndex = targetLane.indexOf(nextTaskId)

  return {
    ...state,
    [sourceLaneId]: laneWithRemovedTask,
    [targetLaneId]: nextTaskId ? [
      ...targetLane.slice(0, targetIndex),
      taskId,
      ...targetLane.slice(targetIndex)
    ] : [...targetLane, taskId]
  }
}

const taskDeleteSuccessMappingsReducer = (state, { taskId }) =>
  mapValues(state, (tasks) => tasks.filter((item) => item !== taskId))

const taskMappingsHandlerMapping = {
  [TASKS_LOAD_SUCCESS]: taskLoadSuccessMappingsReducer,
  [TASK_ADD_SAVE]: taskAddSaveMappingsReducer,
  [TASK_ADD_SAVE_SUCCESS]: taskAddSaveSuccessMappingsReducer,
  [TASK_ADD_SAVE_FAILURE]: taskAddSaveFailureMappingsReducer,
  [TASK_MOVE_PERSIST]: taskMovePersistMappingsReducer,
  [TASK_DELETE_SUCCESS]: taskDeleteSuccessMappingsReducer
}

const taskMappingsInitialState = null
export const taskMappingsReducer = createReducer(taskMappingsInitialState, taskMappingsHandlerMapping)




// WEBPACK FOOTER //
// ./src/store/tasks.js